import { ChevronLeft, ChevronRight, Globe, RotateCw, User, X, Settings, AlertTriangle, Shield, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';

const ChromeBrowserContent = ({ scenario, onComplete }) => {
  const [tabs, setTabs] = useState([
    { id: 1, title: 'New Tab', url: 'chrome://newtab', active: true },
  ]);
  const [activeTabId, setActiveTabId] = useState(1);
  const [addressBarValue, setAddressBarValue] = useState('chrome://newtab');
  const [pageContent, setPageContent] = useState('Welcome to Chrome');
  const [history, setHistory] = useState(['chrome://newtab']);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [showExtensions, setShowExtensions] = useState(false);
  const [extensions, setExtensions] = useState([]);

  useEffect(() => {
    console.log('ChromeBrowserContent mounted/updated', { activeTabId, addressBarValue });

    // Initialize extensions based on scenario
    if (scenario?.browserExtensions) {
      setExtensions(scenario.browserExtensions);
    } else {
      // Default extensions including a suspicious one for malware scenarios
      setExtensions([
        {
          id: 'adblock',
          name: 'AdBlock Plus',
          version: '3.15.1',
          size: '2.1 MB',
          permissions: ['Read and change all your data on all websites'],
          isMalicious: false,
          suspiciousBehaviors: [],
          malwareType: null,
          description: 'Blocks ads and trackers'
        },
        {
          id: 'malicious-extension',
          name: 'Super Ad Blocker Pro',
          version: '2.1.4',
          size: '15.7 MB',
          permissions: ['Read and change all your data on all websites', 'Access your data on all sites'],
          isMalicious: true,
          suspiciousBehaviors: ['Unusually large file size', 'Excessive CPU usage', 'Suspicious network activity'],
          malwareType: 'Cryptominer',
          description: 'Advanced ad blocking with enhanced features'
        }
      ]);
    }
  }, [scenario]);

  const activeTab = tabs.find(t => t.id === activeTabId);

  const handleAddressBarSubmit = (e) => {
    e.preventDefault();
    const newUrl = addressBarValue;

    // Handle special URLs
    if (newUrl === 'chrome://extensions' || newUrl.includes('extensions')) {
      setShowExtensions(true);
      setPageContent('Extensions Management');
      // Emit browser_extensions_opened event when user navigates to extensions page
      if (onComplete) {
        onComplete('browser_extensions_opened', {
          timestamp: Date.now(),
          url: newUrl
        });
      }
      return;
    }

    setTabs(prev => prev.map(t =>
      t.id === activeTabId ? { ...t, url: newUrl, title: newUrl } : t
    ));
    setPageContent(`Content for: ${newUrl}`);

    // Add to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newUrl);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const removeExtension = (extensionId) => {
    const extensionToRemove = extensions.find(ext => ext.id === extensionId);

    setExtensions(prev => prev.filter(ext => ext.id !== extensionId));

    // Emit extension_removed event when any extension is removed
    if (onComplete && extensionToRemove) {
      onComplete('extension_removed', {
        extensionId,
        extensionName: extensionToRemove.name,
        isMalicious: extensionToRemove.isMalicious,
        timestamp: Date.now()
      });
    }

    // Check if all malicious extensions are removed (legacy completion check)
    const remainingMalicious = extensions.filter(ext => ext.isMalicious && ext.id !== extensionId);
    if (remainingMalicious.length === 0 && onComplete) {
      // Also emit completion event for legacy compatibility
      onComplete('all_malicious_extensions_removed', {
        success: true,
        message: 'All malicious extensions removed.',
        correct: 1,
        total: 1,
        score: 100
      });
    }
  };

  const addNewTab = () => {
    const newId = Math.max(...tabs.map(t => t.id)) + 1;
    setTabs(prev => [...prev.map(t => ({ ...t, active: false })),
    { id: newId, title: 'New Tab', url: 'chrome://newtab', active: true }
    ]);
    setActiveTabId(newId);
    setAddressBarValue('chrome://newtab');
    setPageContent('Welcome to Chrome');
  };

  const switchTab = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      setTabs(prev => prev.map(t => ({ ...t, active: t.id === tabId })));
      setActiveTabId(tabId);
      setAddressBarValue(tab.url);
      setPageContent(`Content for: ${tab.url}`);
    }
  };

  const closeTab = (tabId, e) => {
    e.stopPropagation();
    if (tabs.length === 1) return; // Don't close last tab

    const newTabs = tabs.filter(t => t.id !== tabId);
    setTabs(newTabs);

    if (activeTabId === tabId) {
      const newActiveTab = newTabs[0];
      setActiveTabId(newActiveTab.id);
      setAddressBarValue(newActiveTab.url);
      setPageContent(`Content for: ${newActiveTab.url}`);
    }
  };

  const goBack = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const url = history[newIndex];
      setHistoryIndex(newIndex);
      setAddressBarValue(url);
      setPageContent(`Content for: ${url}`);
    }
  };

  const goForward = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const url = history[newIndex];
      setHistoryIndex(newIndex);
      setAddressBarValue(url);
      setPageContent(`Content for: ${url}`);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white text-black">
      {/* Tab Bar */}
      <div className="flex items-center bg-gray-100 border-b border-gray-300">
        {tabs.map(tab => (
          <div
            key={tab.id}
            onClick={() => switchTab(tab.id)}
            className={`flex items-center px-4 py-2 min-w-[150px] max-w-[200px] border-r border-gray-300 cursor-pointer ${tab.id === activeTabId ? 'bg-white' : 'bg-gray-100 hover:bg-gray-200'
              }`}
          >
            <Globe size={14} className="mr-2 flex-shrink-0" />
            <span className="text-xs truncate flex-1">{tab.title}</span>
            <button
              onClick={(e) => closeTab(tab.id, e)}
              className="ml-2 hover:bg-gray-300 rounded p-1"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        <button
          onClick={addNewTab}
          className="px-3 py-2 hover:bg-gray-200"
          title="New tab"
        >
          <span className="text-lg">+</span>
        </button>
      </div>

      {/* Navigation Bar */}
      <div className="flex items-center p-2 border-b border-gray-300 space-x-2 bg-white">
        <button
          onClick={goBack}
          disabled={historyIndex === 0}
          className="p-2 hover:bg-gray-100 rounded disabled:opacity-30"
          title="Back"
        >
          <ChevronLeft size={18} />
        </button>
        <button
          onClick={goForward}
          disabled={historyIndex === history.length - 1}
          className="p-2 hover:bg-gray-100 rounded disabled:opacity-30"
          title="Forward"
        >
          <ChevronRight size={18} />
        </button>
        <button
          onClick={() => setPageContent(`Refreshed: ${addressBarValue}`)}
          className="p-2 hover:bg-gray-100 rounded"
          title="Reload"
        >
          <RotateCw size={18} />
        </button>

        {/* Address Bar */}
        <form onSubmit={handleAddressBarSubmit} className="flex-1">
          <div className="flex items-center bg-gray-100 rounded-full px-4 py-1 border border-gray-300">
            <Globe size={16} className="text-gray-500 mr-2" />
            <input
              type="text"
              value={addressBarValue}
              onChange={(e) => setAddressBarValue(e.target.value)}
              className="flex-1 bg-transparent outline-none text-sm"
              placeholder="Search or type URL"
            />
          </div>
        </form>

        <button className="p-2 hover:bg-gray-100 rounded">
          <User size={18} />
        </button>
      </div>

      {/* Page Content */}
      <div className="flex-1 p-8 overflow-auto bg-white">
        <div className="max-w-4xl mx-auto">
          {showExtensions ? (
            // Extensions Management Page
            <div>
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold">Extensions</h1>
                <button
                  onClick={() => setShowExtensions(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                >
                  Back to Browser
                </button>
              </div>

              <div className="space-y-4">
                {extensions.map((extension) => (
                  <div
                    key={extension.id}
                    className={`p-4 border rounded-lg ${extension.isMalicious
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 bg-white'
                      }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold">{extension.name}</h3>
                          {extension.isMalicious && (
                            <AlertTriangle size={20} className="text-red-500" />
                          )}
                        </div>

                        <div className="text-sm text-gray-600 mb-2">
                          <p>Version: {extension.version}</p>
                          <p>Size: {extension.size}</p>
                          <p>{extension.description}</p>
                        </div>

                        {extension.isMalicious && (
                          <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded">
                            <div className="flex items-center mb-2">
                              <Shield size={16} className="text-red-600 mr-2" />
                              <span className="font-bold text-red-800">Suspicious Extension</span>
                            </div>
                            <p className="text-red-700 text-sm mb-2">
                              <strong>Malware Type:</strong> {extension.malwareType}
                            </p>
                            <div className="text-red-700 text-sm">
                              <strong>Suspicious Behaviors:</strong>
                              <ul className="list-disc list-inside mt-1">
                                {extension.suspiciousBehaviors.map((behavior, index) => (
                                  <li key={index}>{behavior}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        )}

                        <div className="mt-3">
                          <p className="text-sm font-medium text-gray-700 mb-1">Permissions:</p>
                          <div className="text-sm text-gray-600">
                            {extension.permissions.map((permission, index) => (
                              <div key={index} className="flex items-center">
                                <span className="w-2 h-2 bg-yellow-400 rounded-full mr-2"></span>
                                {permission}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="ml-4">
                        <button
                          onClick={() => removeExtension(extension.id)}
                          className={`px-4 py-2 rounded text-sm font-medium ${extension.isMalicious
                              ? 'bg-red-600 text-white hover:bg-red-700'
                              : 'bg-gray-500 text-white hover:bg-gray-600'
                            }`}
                        >
                          <Trash2 size={16} className="inline mr-1" />
                          Remove
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            // Default Browser Page
            <div>
              <h1 className="text-3xl font-bold mb-4">Chrome Browser Simulator</h1>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Debug Info:</strong> Active Tab ID: {activeTabId} | Total Tabs: {tabs.length}
                </p>
                <p className="text-sm text-blue-800">
                  History Index: {historyIndex} / {history.length - 1}
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded border border-gray-200">
                <p className="text-lg font-medium mb-2">{pageContent}</p>
                <p className="text-sm text-gray-600">Current URL: {addressBarValue}</p>

                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">Test Actions:</p>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => {
                        setAddressBarValue('chrome://extensions');
                        handleAddressBarSubmit(new Event('submit'));
                      }}
                      className="px-3 py-1 bg-purple-500 text-white rounded text-sm hover:bg-purple-600"
                    >
                      Open Extensions
                    </button>
                    <button
                      onClick={() => {
                        setAddressBarValue('https://example.com');
                        handleAddressBarSubmit(new Event('submit'));
                      }}
                      className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                    >
                      Go to example.com
                    </button>
                    <button
                      onClick={() => {
                        setAddressBarValue('https://github.com');
                        handleAddressBarSubmit(new Event('submit'));
                      }}
                      className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                    >
                      Go to github.com
                    </button>
                    <button
                      onClick={addNewTab}
                      className="px-3 py-1 bg-green-500 text-white rounded text-sm hover:bg-green-600"
                    >
                      Add New Tab
                    </button>
                  </div>
                </div>

                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-xs text-yellow-800">
                    <strong>State Test:</strong> Change tabs or navigate to different URLs, then try moving this window.
                    If the state resets (tabs/URL change), there's a state preservation issue.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChromeBrowserContent;